import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaUsers, FaWallet, FaBoxOpen, FaSpinner, FaMoneyBillWave, FaFileInvoiceDollar, FaHandshake, FaExclamationTriangle } from 'react-icons/fa';
import paymentPromiseService from '../services/paymentPromiseService';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title
} from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import currentAccountService from '../services/currentAccountService';
import cashService from '../services/cashService';
import productService from '../services/productService';
import paymentService from '../services/paymentService';

// ChartJS bileşenlerini kaydet
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

const DashboardPage = () => {
  const navigate = useNavigate();
  const [promiseDashboard, setPromiseDashboard] = useState({ today: [], overdue: [], upcoming: [] });

  const [stats, setStats] = useState({
    totalAccounts: 0,
    cashBalance: 0,
    totalProducts: 0,
    totalReceivable: 0,
    totalPayable: 0,
    cashSummary: { total_in: 0, total_out: 0 },
    accountDistribution: { musteri: 0, tedarikci: 0 },
    recentAccounts: [],
    recentPayments: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Tüm verileri paralel olarak çek (Daha hızlı yüklenmesi için)
        const [accounts, cashSummary, products, payments, promiseData] = await Promise.all([
          currentAccountService.getAll(),
          cashService.getSummary(),
          productService.getAll(),
          paymentService.getAll(),
          paymentPromiseService.getDashboard().catch(() => ({ today: [], overdue: [], upcoming: [] }))
        ]);
        setPromiseDashboard(promiseData);

        const customerCount = accounts.filter(a => a.account_type === 'musteri').length;
        const supplierCount = accounts.filter(a => a.account_type === 'tedarikci').length;

        // Alacak ve Borç Hesaplama
        let totalReceivable = 0;
        let totalPayable = 0;

        accounts.forEach(acc => {
          const balance = parseFloat(acc.balance) || 0;
          if (acc.account_type === 'musteri') {
            // Müşteri: Pozitif = Bize Borçlu (Alacak), Negatif = Biz Borçluyuz (Borç/Avans)
            if (balance > 0) totalReceivable += balance;
            else totalPayable += Math.abs(balance);
          } else {
            // Tedarikçi: Pozitif = Biz Borçluyuz (Borç), Negatif = Bize Borçlu (Alacak/Avans)
            if (balance > 0) totalPayable += balance;
            else totalReceivable += Math.abs(balance);
          }
        });

        setStats({
          totalAccounts: accounts.length,
          cashBalance: cashSummary.balance,
          totalProducts: products.length,
          totalReceivable,
          totalPayable,
          cashSummary: cashSummary,
          accountDistribution: { musteri: customerCount, tedarikci: supplierCount },
          recentAccounts: accounts.slice(0, 5),
          recentPayments: payments.slice(0, 5)
        });
      } catch (error) {
        console.error('Dashboard veri hatası:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // --- GRAFİK VERİLERİ ---
  
  // 1. Gelir / Gider Grafiği (Doughnut)
  const cashChartData = {
    labels: ['Toplam Giriş', 'Toplam Çıkış'],
    datasets: [
      {
        data: [stats.cashSummary.total_in, stats.cashSummary.total_out],
        backgroundColor: ['#48C9B0', '#EC7063'], // Soft Yeşil, Soft Kırmızı
        hoverBackgroundColor: ['#1ABC9C', '#CB4335'],
        borderWidth: 0,
        hoverOffset: 5,
      },
    ],
  };

  // 2. Cari Dağılımı (Bar)
  const accountChartData = {
    labels: ['Müşteri', 'Tedarikçi'],
    datasets: [
      {
        label: 'Kayıt Sayısı',
        data: [stats.accountDistribution.musteri, stats.accountDistribution.tedarikci],
        backgroundColor: ['#5DADE2', '#F5B041'], // Soft Mavi, Soft Turuncu
        borderRadius: 5,
      },
    ],
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <FaSpinner className="spinner-border text-primary" />
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4">
      <h2 className="mb-4 text-secondary">Genel Bakış</h2>
      
      <div className="row g-3">
        {/* Cari Kartı */}
        <div className="col-md">
          <div className="card text-white border-0 shadow mb-3" style={{ backgroundColor: '#6495ED', borderRadius: '15px' }}>
            <div className="card-body d-flex align-items-center justify-content-between">
              <div>
                <h5 className="card-title">Toplam Cari</h5>
                <h4 className="fw-bold">{stats.totalAccounts}</h4>
              </div>
              <FaUsers size={40} className="opacity-50" />
            </div>
          </div>
        </div>

        {/* Toplam Alacak Kartı */}
        <div className="col-md">
          <div className="card text-white border-0 shadow mb-3" style={{ backgroundColor: '#48C9B0', borderRadius: '15px' }}>
            <div className="card-body d-flex align-items-center justify-content-between">
              <div>
                <h5 className="card-title">Toplam Alacak</h5>
                <h4 className="fw-bold">
                  {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(stats.totalReceivable)}
                </h4>
              </div>
              <FaMoneyBillWave size={40} className="opacity-50" />
            </div>
          </div>
        </div>

        {/* Toplam Borç Kartı */}
        <div className="col-md">
          <div className="card text-white border-0 shadow mb-3" style={{ backgroundColor: '#EC7063', borderRadius: '15px' }}>
            <div className="card-body d-flex align-items-center justify-content-between">
              <div>
                <h5 className="card-title">Toplam Borç</h5>
                <h4 className="fw-bold">
                  {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(stats.totalPayable)}
                </h4>
              </div>
              <FaFileInvoiceDollar size={40} className="opacity-50" />
            </div>
          </div>
        </div>

        {/* Kasa Kartı */}
        <div className="col-md">
          <div className="card text-white border-0 shadow mb-3" style={{ backgroundColor: '#5DADE2', borderRadius: '15px' }}>
            <div className="card-body d-flex align-items-center justify-content-between">
              <div>
                <h5 className="card-title">Kasa Bakiyesi</h5>
                <h4 className="fw-bold">
                  {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(stats.cashBalance)}
                </h4>
              </div>
              <FaWallet size={40} className="opacity-50" />
            </div>
          </div>
        </div>

        {/* Stok Kartı */}
        <div className="col-md">
          <div className="card text-white border-0 shadow mb-3" style={{ backgroundColor: '#F5B041', borderRadius: '15px' }}>
            <div className="card-body d-flex align-items-center justify-content-between">
              <div>
                <h5 className="card-title">Toplam Ürün</h5>
                <h4 className="fw-bold">{stats.totalProducts}</h4>
              </div>
              <FaBoxOpen size={40} className="opacity-50" />
            </div>
          </div>
        </div>
      </div>

      {/* Grafikler Alanı */}
      <div className="row mt-4">
        <div className="col-md-6 mb-4">
          <div className="card shadow border-0 h-100" style={{ borderRadius: '15px', overflow: 'hidden' }}>
            <div className="card-header bg-white border-0 pt-4 ps-4">
              <h5 className="mb-0 fw-bold text-secondary">Gelir / Gider Durumu</h5>
            </div>
            <div className="card-body d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '350px' }}>
              <div className="position-relative" style={{ height: '220px', width: '100%' }}>
                <Doughnut 
                  data={cashChartData} 
                  options={{ 
                    maintainAspectRatio: false, 
                    cutout: '75%', 
                    plugins: { 
                      legend: { display: false },
                      tooltip: {
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        titleColor: '#2c3e50',
                        bodyColor: '#2c3e50',
                        borderColor: '#e9ecef',
                        borderWidth: 1,
                        padding: 10,
                        boxPadding: 4,
                        callbacks: {
                          label: function(context) {
                            return context.label + ': ' + new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(context.parsed);
                          }
                        }
                      }
                    } 
                  }} 
                />
                <div className="position-absolute top-50 start-50 translate-middle text-center" style={{ pointerEvents: 'none' }}>
                  <small className="text-muted d-block mb-1">Net Durum</small>
                  <span className={`fs-5 fw-bold ${stats.cashBalance >= 0 ? 'text-success' : 'text-danger'}`}>
                    {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(stats.cashBalance)}
                  </span>
                </div>
              </div>
              <div className="d-flex justify-content-center gap-4 mt-4 w-100">
                <div className="d-flex align-items-center p-2 rounded bg-light bg-opacity-50">
                  <span style={{ width: '10px', height: '10px', backgroundColor: '#48C9B0', borderRadius: '50%', display: 'inline-block', marginRight: '8px' }}></span>
                  <span className="text-muted small fw-bold">Giriş: <span className="text-dark">{new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(stats.cashSummary.total_in)}</span></span>
                </div>
                <div className="d-flex align-items-center p-2 rounded bg-light bg-opacity-50">
                  <span style={{ width: '10px', height: '10px', backgroundColor: '#EC7063', borderRadius: '50%', display: 'inline-block', marginRight: '8px' }}></span>
                  <span className="text-muted small fw-bold">Çıkış: <span className="text-dark">{new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(stats.cashSummary.total_out)}</span></span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-6 mb-4">
          <div className="card shadow border-0 h-100" style={{ borderRadius: '15px' }}>
            <div className="card-header bg-white">
              <h5 className="mb-0">Müşteri & Tedarikçi Dağılımı</h5>
            </div>
            <div className="card-body" style={{ maxHeight: '300px' }}>
              <Bar 
                data={accountChartData} 
                options={{ 
                  maintainAspectRatio: false, 
                  plugins: { 
                    legend: { display: false },
                    tooltip: {
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      titleColor: '#2c3e50',
                      bodyColor: '#2c3e50',
                      borderColor: '#e9ecef',
                      borderWidth: 1,
                      padding: 10,
                      callbacks: {
                        label: function(context) {
                          return `Toplam: ${context.parsed.y} Kayıt`;
                        }
                      }
                    }
                  } 
                }} 
              />
            </div>
          </div>
        </div>
      </div>

      {/* Ödeme Sözü Uyarıları */}
      {(promiseDashboard.today.length > 0 || promiseDashboard.overdue.length > 0) && (
        <div className="row mt-4">
          {promiseDashboard.today.length > 0 && (
            <div className="col-md-6 mb-4">
              <div className="card border-warning shadow" style={{ borderLeft: '5px solid #ffc107', borderRadius: 15 }}>
                <div className="card-header bg-warning bg-opacity-10 border-0 d-flex justify-content-between align-items-center">
                  <h6 className="mb-0 fw-bold text-warning"><FaHandshake className="me-2" />Bugün Vadesi Gelen Ödeme Sözleri</h6>
                  <span className="badge bg-warning text-dark">{promiseDashboard.today.length}</span>
                </div>
                <div className="card-body p-0">
                  <table className="table table-sm mb-0">
                    <tbody>
                      {promiseDashboard.today.map(p => (
                        <tr key={p.id} className="table-warning">
                          <td className="fw-semibold ps-3">{p.company_name}</td>
                          <td className="text-end fw-bold pe-3">
                            {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(p.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="card-footer bg-white border-0">
                  <Link to="/payment-promises" className="btn btn-sm btn-warning w-100">Tümünü Gör</Link>
                </div>
              </div>
            </div>
          )}
          {promiseDashboard.overdue.length > 0 && (
            <div className="col-md-6 mb-4">
              <div className="card border-danger shadow" style={{ borderLeft: '5px solid #dc3545', borderRadius: 15 }}>
                <div className="card-header bg-danger bg-opacity-10 border-0 d-flex justify-content-between align-items-center">
                  <h6 className="mb-0 fw-bold text-danger"><FaExclamationTriangle className="me-2" />Geciken Ödeme Sözleri</h6>
                  <span className="badge bg-danger">{promiseDashboard.overdue.length}</span>
                </div>
                <div className="card-body p-0">
                  <table className="table table-sm mb-0">
                    <tbody>
                      {promiseDashboard.overdue.map(p => (
                        <tr key={p.id} className="table-danger">
                          <td className="ps-3">
                            <div className="fw-semibold">{p.company_name}</div>
                            <small className="text-muted">{new Date(String(p.promise_date).split('T')[0] + 'T12:00:00').toLocaleDateString('tr-TR')}</small>
                          </td>
                          <td className="text-end fw-bold pe-3">
                            {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(p.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="card-footer bg-white border-0">
                  <Link to="/payment-promises" className="btn btn-sm btn-danger w-100">Tümünü Gör</Link>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Son İşlemler (Tahsilat/Ödeme) */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card shadow border-0" style={{ borderRadius: '15px' }}>
            <div className="card-header bg-white border-0 pt-4 ps-4 d-flex justify-content-between align-items-center">
              <h5 className="mb-0 fw-bold text-secondary">Son İşlemler (Tahsilat/Ödeme)</h5>
              <Link to="/payments" className="btn btn-sm btn-outline-primary">Tümünü Gör</Link>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-hover align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Tarih</th>
                      <th>Cari Hesap</th>
                      <th>İşlem Tipi</th>
                      <th>Tutar</th>
                      <th>Yöntem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recentPayments && stats.recentPayments.map(payment => (
                      <tr key={payment.id} onClick={() => navigate(`/payments/edit/${payment.id}`)} style={{ cursor: 'pointer' }}>
                        <td>{new Date(payment.payment_date).toLocaleDateString('tr-TR')}</td>
                        <td className="fw-bold">{payment.company_name}</td>
                        <td>
                          <span className={`badge ${payment.payment_type === 'tahsilat' ? 'bg-success' : 'bg-danger'}`}>
                            {payment.payment_type === 'tahsilat' ? 'Tahsilat' : 'Ödeme'}
                          </span>
                        </td>
                        <td className="fw-bold">
                          {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(payment.amount)}
                        </td>
                        <td>{payment.payment_method === 'nakit' ? 'Nakit' : 'Banka'}</td>
                      </tr>
                    ))}
                    {(!stats.recentPayments || stats.recentPayments.length === 0) && (
                      <tr>
                        <td colSpan="5" className="text-center text-muted py-3">Henüz kayıtlı işlem bulunamadı.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Son Eklenen Cariler */}
      <div className="row mt-4 mb-4">
        <div className="col-12">
          <div className="card shadow border-0" style={{ borderRadius: '15px' }}>
            <div className="card-header bg-white border-0 pt-4 ps-4 d-flex justify-content-between align-items-center">
              <h5 className="mb-0 fw-bold text-secondary">Son Eklenen Cari Hesaplar</h5>
              <Link to="/current-accounts" className="btn btn-sm btn-outline-primary">Tümünü Gör</Link>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-hover align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Firma Adı</th>
                      <th>Yetkili</th>
                      <th>Tip</th>
                      <th>Telefon</th>
                      <th>Kayıt Tarihi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recentAccounts && stats.recentAccounts.map(account => (
                      <tr key={account.id}>
                        <td className="fw-bold">{account.company_name}</td>
                        <td>{account.authorized_person || '-'}</td>
                        <td>
                          <span className={`badge ${account.account_type === 'musteri' ? 'bg-info' : 'bg-warning'}`}>
                            {account.account_type === 'musteri' ? 'Müşteri' : 'Tedarikçi'}
                          </span>
                        </td>
                        <td>{account.phone || '-'}</td>
                        <td>{new Date(account.created_at).toLocaleDateString('tr-TR')}</td>
                      </tr>
                    ))}
                    {(!stats.recentAccounts || stats.recentAccounts.length === 0) && (
                      <tr>
                        <td colSpan="5" className="text-center text-muted py-3">Henüz kayıtlı cari hesap bulunamadı.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
